import type { ComboboxItemWithValue } from "@replohq/lattice/components/combobox/Combobox";
import type { AgentBetaAccountMode } from "./components/AccountStep";
import type {
  AgentBetaFormStep,
  AgentBetaFormValues,
} from "./useAgentBetaWaitlistForm";

import * as React from "react";

import { Alert } from "@replohq/lattice/blocks/alert/Alert";
import { LabeledControl } from "@replohq/lattice/blocks/labeled-control/LabeledControl";
import { SelectionItem } from "@replohq/lattice/blocks/selection/SelectionItem";
import { Button } from "@replohq/lattice/components/button/Button";
import {
  ComboboxChip,
  ComboboxChips,
  ComboboxChipsInput,
  ComboboxContent,
  ComboboxEmpty,
  ComboboxItem,
  ComboboxList,
  ComboboxRoot,
  ComboboxValue,
} from "@replohq/lattice/components/combobox/Combobox";
import { Input } from "@replohq/lattice/components/input/Input";
import {
  Menu,
  MenuContent,
  MenuRadioGroup,
  MenuRadioItem,
  MenuTrigger,
} from "@replohq/lattice/components/menu/Menu";
import {
  ProgressIndicator,
  ProgressRoot,
  ProgressTrack,
} from "@replohq/lattice/components/progress/Progress";
import { Separator } from "@replohq/lattice/components/separator/Separator";
import { cn } from "@replohq/lattice/utils/cn";
import { DURATION, EASING } from "@replohq/lattice/utils/motion";
import { routes } from "@v2/utils/router";
import { ChevronDown, ChevronLeft } from "lucide-react";
import { motion, useReducedMotion } from "motion/react";
import { Controller } from "react-hook-form";
import { useSearchParams } from "react-router-dom";
import {
  AGENT_BETA_OTHER_ANSWER_MAX_LENGTH,
  AGENT_BETA_OTHER_OPTION_VALUE,
  agentBetaAnnualRevenueBuckets,
  agentBetaMultiSelectQuestions,
} from "schemas/agentBeta";

import { AccountStep } from "./components/AccountStep";
import { AgentBetaPageShell } from "./components/AgentBetaPageShell";
import { BetaIntroVideo } from "./components/BetaIntroVideo";
import { StepActions } from "./components/StepActions";
import { useAgentBetaWaitlistForm } from "./useAgentBetaWaitlistForm";

const businessTypeOptions = [
  { label: "Business", value: "business" },
  { label: "Agency or Freelancer", value: "agency" },
  { label: "Just Exploring", value: "other" },
] as const;

const annualRevenueOptions = agentBetaAnnualRevenueBuckets.map(
  (bucket, index) => ({
    label: bucket.label,
    value: String(index),
  }),
);

function getStepHeading({
  step,
  accountMode,
  profileTitle,
}: {
  step: AgentBetaFormStep;
  accountMode: AgentBetaAccountMode;
  profileTitle?: string;
}): { title: string; subtitle: string | null } {
  if (step === "profile") {
    return {
      title: profileTitle || "Join the Replo Agent Beta",
      subtitle: null,
    };
  }
  if (step === "account") {
    return accountMode === "signup"
      ? {
          title: "Create your account",
          subtitle: "Already used this email? We'll log you straight in.",
        }
      : {
          title: "Welcome back",
          subtitle: "Sign in and we'll pin your spot to this account.",
        };
  }
  return {
    title: "Choose your workspaces",
    subtitle: "Pick which workspaces get early access turned on.",
  };
}

// NOTE (Yuxin, 2026-07-30): Authenticated users skip the account step, so the
// progress total is derived from auth state rather than a fixed "of 3" — this
// keeps the bar advancing smoothly instead of jumping from step 1 to step 3.
function getStepProgress({
  step,
  isAuthenticated,
}: {
  step: AgentBetaFormStep;
  isAuthenticated: boolean;
}): { currentStep: number; totalSteps: number } {
  const stepOrder: AgentBetaFormStep[] = isAuthenticated
    ? ["profile", "workspace"]
    : ["profile", "account", "workspace"];
  const stepIndex = stepOrder.indexOf(step);
  return {
    currentStep: stepIndex === -1 ? 1 : stepIndex + 1,
    totalSteps: stepOrder.length,
  };
}

const AgentBetaWaitlistForm: React.FC<{
  workspaceId?: string;
  variant?: "page" | "modal";
  onDismiss?: () => void;
  profileTitle?: string;
}> = ({ workspaceId, variant = "page", onDismiss, profileTitle }) => {
  const {
    form,
    step,
    accountMode,
    isAuthenticated,
    isLoadingUser,
    submitState,
    workspaceOptions,
    selectedWorkspaceIds,
    setAccountMode,
    setWorkspaceSelected,
    selectAllWorkspaces,
    continueFromProfile,
    submitAccount,
    submitWithGoogle,
    submitSelectedWorkspaces,
    goBack,
  } = useAgentBetaWaitlistForm({
    workspaceId,
    source: variant === "modal" ? "inviteModal" : "waitlistPage",
  });
  const isSubmitting = submitState.status === "submitting";
  const stampAudio = useStampAudio();

  if (isLoadingUser) {
    return (
      <div className="flex min-h-64 items-center justify-center">
        <p className="text-sm text-muted">Getting things ready…</p>
      </div>
    );
  }

  if (submitState.status === "success") {
    return (
      <BetaSuccess variant={variant} onStampImpact={stampAudio.playStamp} />
    );
  }

  const heading = getStepHeading({ step, accountMode, profileTitle });
  const { currentStep, totalSteps } = getStepProgress({
    step,
    isAuthenticated,
  });

  return (
    <div
      className={cn(
        "flex w-full flex-col gap-6",
        variant === "modal" && "flex-1",
      )}
      onPointerDownCapture={stampAudio.unlock}
      onKeyDownCapture={stampAudio.unlock}
    >
      {step !== "profile" && (
        <button
          type="button"
          onClick={goBack}
          disabled={isSubmitting}
          className="flex w-fit items-center gap-1 text-sm text-muted transition-colors hover:text-default disabled:opacity-50"
        >
          <ChevronLeft className="size-4" aria-hidden="true" />
          Back
        </button>
      )}
      <div className="flex flex-col gap-3">
        <ProgressRoot
          value={currentStep}
          max={totalSteps}
          aria-label={`Step ${currentStep} of ${totalSteps}`}
          className="max-w-40"
        >
          <ProgressTrack>
            <ProgressIndicator />
          </ProgressTrack>
        </ProgressRoot>
        <div className="flex flex-col gap-2">
          <h1
            className={cn(
              "font-editorial font-bold leading-tight text-default",
              variant === "page" ? "text-3xl md:text-4xl" : "text-2xl",
            )}
          >
            {heading.title}
          </h1>
          {heading.subtitle && (
            <p className="text-sm leading-5 text-muted">{heading.subtitle}</p>
          )}
        </div>
      </div>

      {submitState.status === "error" && (
        <Alert variant="error" description={submitState.message} />
      )}

      {step === "profile" && (
        <ProfileStep
          form={form}
          isAuthenticated={isAuthenticated}
          isSubmitting={isSubmitting}
          onSubmit={continueFromProfile}
          variant={variant}
          onDismiss={onDismiss}
        />
      )}
      {step === "account" && (
        <AccountStep
          emailProps={form.register("email")}
          passwordProps={form.register("password")}
          emailError={form.formState.errors.email?.message}
          passwordError={form.formState.errors.password?.message}
          accountMode={accountMode}
          isSubmitting={isSubmitting}
          onModeChange={setAccountMode}
          onSubmit={() => void form.handleSubmit(submitAccount)()}
          onGoogleSubmit={submitWithGoogle}
        />
      )}
      {step === "workspace" && (
        <WorkspaceStep
          workspaceOptions={workspaceOptions}
          selectedWorkspaceIds={selectedWorkspaceIds}
          isSubmitting={isSubmitting}
          onWorkspaceSelectionChange={setWorkspaceSelected}
          onSelectAll={selectAllWorkspaces}
          onSubmit={submitSelectedWorkspaces}
        />
      )}
    </div>
  );
};

const ProfileStep: React.FC<{
  form: ReturnType<typeof useAgentBetaWaitlistForm>["form"];
  isAuthenticated: boolean;
  isSubmitting: boolean;
  onSubmit: (values: AgentBetaFormValues) => Promise<void>;
  variant: "page" | "modal";
  onDismiss?: () => void;
}> = ({
  form,
  isAuthenticated,
  isSubmitting,
  onSubmit,
  variant,
  onDismiss,
}) => {
  const {
    register,
    control,
    handleSubmit,
    formState: { errors },
  } = form;
  const submitLabel =
    variant === "modal" || isAuthenticated ? "Request access" : "Next";

  return (
    <form
      className={cn(
        "flex w-full flex-col gap-5",
        variant === "modal" && "flex-1",
      )}
      onSubmit={(event) => {
        void handleSubmit(onSubmit)(event);
      }}
    >
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
        <LabeledControl
          label="First name"
          size="base"
          id="firstName"
          error={errors.firstName?.message}
        >
          <Input
            id="firstName"
            size="base"
            placeholder="Ada"
            autoComplete="given-name"
            autoFocus
            invalid={Boolean(errors.firstName)}
            {...register("firstName")}
          />
        </LabeledControl>
        <LabeledControl
          label="Last name"
          size="base"
          id="lastName"
          error={errors.lastName?.message}
        >
          <Input
            id="lastName"
            size="base"
            placeholder="Lovelace"
            autoComplete="family-name"
            invalid={Boolean(errors.lastName)}
            {...register("lastName")}
          />
        </LabeledControl>
      </div>

      <LabeledControl
        label="Work email"
        size="base"
        id="email"
        error={errors.email?.message}
      >
        <Input
          id="email"
          type="email"
          size="base"
          placeholder="ada@yourbrand.com"
          autoComplete="email"
          disabled={isAuthenticated}
          invalid={Boolean(errors.email)}
          {...register("email")}
        />
      </LabeledControl>

      <LabeledControl
        label="Team type"
        size="base"
        id="businessType"
        error={errors.profileType?.message}
      >
        <Controller
          name="profileType"
          control={control}
          render={({ field }) => (
            <DropdownField
              id="businessType"
              placeholder="Select a team type"
              options={businessTypeOptions}
              value={field.value ?? ""}
              onValueChange={field.onChange}
              invalid={Boolean(errors.profileType)}
            />
          )}
        />
      </LabeledControl>

      <LabeledControl
        label="Annual revenue"
        size="base"
        id="annualRevenue"
        error={errors.revenueBucketIndex?.message}
      >
        <Controller
          name="revenueBucketIndex"
          control={control}
          render={({ field }) => (
            <DropdownField
              id="annualRevenue"
              placeholder="Select annual revenue"
              options={annualRevenueOptions}
              value={field.value === null ? "" : String(field.value)}
              onValueChange={(value) => {
                field.onChange(Number(value));
              }}
              invalid={Boolean(errors.revenueBucketIndex)}
            />
          )}
        />
      </LabeledControl>

      {agentBetaMultiSelectQuestions.map((question) => (
        <Controller
          key={question.slug}
          name={`questionAnswers.${question.slug}`}
          control={control}
          render={({ field, fieldState }) => (
            <LabeledControl
              label={question.label}
              size="base"
              id={question.slug}
              error={fieldState.error?.message}
            >
              <div className="flex flex-col gap-2">
                <MultiSelectField
                  id={question.slug}
                  placeholder={question.placeholder}
                  options={question.options}
                  value={field.value ?? []}
                  onValueChange={field.onChange}
                  invalid={Boolean(fieldState.error)}
                />
                {(field.value ?? []).includes(
                  AGENT_BETA_OTHER_OPTION_VALUE,
                ) && (
                  <Input
                    size="base"
                    placeholder="Tell us which one"
                    aria-label={`${question.label} — other`}
                    maxLength={AGENT_BETA_OTHER_ANSWER_MAX_LENGTH}
                    {...register(`questionOtherText.${question.slug}`)}
                  />
                )}
              </div>
            </LabeledControl>
          )}
        />
      ))}

      <div
        className={cn("flex flex-col gap-4", variant === "modal" && "mt-auto")}
      >
        {variant === "modal" && <Separator />}
        <StepActions
          submitLabel={submitLabel}
          isSubmitting={isSubmitting}
          onDismiss={variant === "modal" ? onDismiss : undefined}
        />
      </div>
    </form>
  );
};

const WorkspaceStep: React.FC<{
  workspaceOptions: { id: string; name: string }[];
  selectedWorkspaceIds: string[];
  isSubmitting: boolean;
  onWorkspaceSelectionChange: (selection: {
    workspaceId: string;
    selected: boolean;
  }) => void;
  onSelectAll: () => void;
  onSubmit: () => Promise<void>;
}> = ({
  workspaceOptions,
  selectedWorkspaceIds,
  isSubmitting,
  onWorkspaceSelectionChange,
  onSelectAll,
  onSubmit,
}) => {
  const allWorkspacesSelected =
    selectedWorkspaceIds.length === workspaceOptions.length;

  return (
    <div className="flex w-full flex-col gap-5">
      <div className="flex flex-col gap-3">
        <div className="flex justify-end">
          <Button
            type="button"
            variant="secondary"
            size="base"
            disabled={isSubmitting || allWorkspacesSelected}
            onClick={onSelectAll}
          >
            Select all
          </Button>
        </div>
        <fieldset
          className="flex max-h-72 min-w-0 flex-col gap-3 overflow-y-auto border-0 p-0 pr-1"
          aria-label="Workspaces"
        >
          {workspaceOptions.map((workspace) => (
            <SelectionItem
              key={workspace.id}
              type="checkbox"
              id={workspace.id}
              label={workspace.name}
              variant="outline"
              checked={selectedWorkspaceIds.includes(workspace.id)}
              onCheckedChange={(selected) => {
                onWorkspaceSelectionChange({
                  workspaceId: workspace.id,
                  selected,
                });
              }}
            />
          ))}
        </fieldset>
      </div>
      <StepActions
        submitLabel="Get early access"
        isSubmitting={isSubmitting}
        submitDisabled={selectedWorkspaceIds.length === 0}
        onSubmit={() => void onSubmit()}
      />
    </div>
  );
};

const DropdownField: React.FC<{
  id: string;
  placeholder: string;
  options: readonly { label: string; value: string }[];
  value: string;
  invalid: boolean;
  onValueChange: (value: string) => void;
}> = ({ id, placeholder, options, value, invalid, onValueChange }) => {
  const [isOpen, setIsOpen] = React.useState(false);
  const selectedOption = options.find((option) => option.value === value);

  return (
    <Menu open={isOpen} onOpenChange={setIsOpen}>
      <MenuTrigger
        render={
          <Button
            id={id}
            type="button"
            variant="secondary"
            size="base"
            className="w-full"
            UNSAFE_className="justify-between bg-sheet"
            aria-invalid={invalid}
            endEnhancer={
              <ChevronDown className="size-4 text-muted" aria-hidden="true" />
            }
          >
            <span
              className={selectedOption ? "text-default" : "text-placeholder"}
            >
              {selectedOption?.label ?? placeholder}
            </span>
          </Button>
        }
      />
      <MenuContent align="start">
        <MenuRadioGroup
          value={value}
          onValueChange={(nextValue) => {
            onValueChange(nextValue);
            setIsOpen(false);
          }}
        >
          {options.map((option) => (
            <MenuRadioItem
              key={option.value}
              value={option.value}
              label={option.label}
              size="base"
            />
          ))}
        </MenuRadioGroup>
      </MenuContent>
    </Menu>
  );
};

// NOTE (Max, 2026-07-30): Searchable multi-select built on lattice's Combobox —
// inline chips keep the field compact (helps the form stay above the fold) and
// it renders as a bottom sheet on mobile for free.
const MultiSelectField: React.FC<{
  id: string;
  placeholder: string;
  options: readonly { label: string; value: string }[];
  value: string[];
  invalid?: boolean;
  onValueChange: (value: string[]) => void;
}> = ({ id, placeholder, options, value, invalid, onValueChange }) => {
  const anchorRef = React.useRef<HTMLDivElement>(null);
  const items: ComboboxItemWithValue[] = options.map((option) => ({
    value: option.value,
    label: option.label,
  }));
  const valueItems = items.filter((item) => value.includes(item.value));

  return (
    <div ref={anchorRef} className="min-w-0">
      <ComboboxRoot
        items={items}
        multiple
        value={valueItems}
        onValueChange={(next: ComboboxItemWithValue[]) => {
          onValueChange(next.map((item) => item.value));
        }}
        itemToStringValue={(item: ComboboxItemWithValue) => {
          return String(item.label);
        }}
      >
        <ComboboxChips
          aria-invalid={invalid}
          UNSAFE_className="gap-1.5 border-0.5 border-border! bg-sheet px-3! py-2! has-data-[slot=combobox-chip]:px-2! aria-[invalid=true]:border-rose-border! aria-[invalid=true]:bg-rose-soft"
        >
          <ComboboxValue>
            {valueItems.map((item) => (
              <ComboboxChip key={item.value} UNSAFE_className="px-2.5">
                {item.label}
              </ComboboxChip>
            ))}
          </ComboboxValue>
          <ComboboxChipsInput
            id={id}
            placeholder={valueItems.length === 0 ? placeholder : undefined}
          />
        </ComboboxChips>
        <ComboboxContent anchor={anchorRef}>
          <ComboboxEmpty
            title="No matches"
            description="Try a different search."
          />
          <ComboboxList>
            {(item: ComboboxItemWithValue) => (
              <ComboboxItem key={item.value} {...item} value={item} />
            )}
          </ComboboxList>
        </ComboboxContent>
      </ComboboxRoot>
    </div>
  );
};

const BetaSuccess: React.FC<{
  variant: "page" | "modal";
  onStampImpact: () => void;
}> = ({ variant, onStampImpact }) => {
  const shouldReduceMotion = useReducedMotion();

  return (
    <section
      className={cn(
        "relative flex w-full min-w-0 flex-col items-center justify-center text-center",
        variant === "page" ? "min-h-[28rem]" : "min-h-[24rem]",
      )}
      aria-labelledby="beta-success-heading"
      aria-live="polite"
    >
      <motion.img
        src="/images/agent-beta/rsvp-ticket.svg"
        alt=""
        draggable={false}
        initial={
          shouldReduceMotion
            ? false
            : { opacity: 0, scale: 1.35, y: -48, rotate: -18 }
        }
        animate={{ opacity: 1, scale: 1, y: 0, rotate: -4 }}
        transition={{
          delay: shouldReduceMotion ? 0 : DURATION.fast,
          duration: shouldReduceMotion ? 0 : DURATION.standard,
          ease: EASING.outQuint,
        }}
        onAnimationComplete={onStampImpact}
        className="pointer-events-none -mb-5 h-auto w-64"
        aria-hidden="true"
      />
      <motion.div
        initial={shouldReduceMotion ? false : { opacity: 0, y: 8 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{
          delay: shouldReduceMotion ? 0 : DURATION.slow,
          duration: shouldReduceMotion ? 0 : DURATION.standard,
          ease: EASING.outQuint,
        }}
        className="relative flex w-full max-w-xl flex-col items-center gap-5"
      >
        <h1
          id="beta-success-heading"
          className={cn(
            "font-editorial font-bold italic leading-none text-default",
            variant === "page" ? "text-5xl" : "text-4xl",
          )}
        >
          You're in.
        </h1>
        <p className="max-w-md text-sm leading-5 text-muted">
          You're one of the first in. Jump into our Slack to trade ideas with
          other founders and builders — and help shape what ships next.
        </p>
        <div className="flex w-full flex-row items-center justify-center gap-2">
          <Button
            variant="primary"
            size="base"
            render={
              <a
                href="https://airtable.com/appzX9luz6we5Lfnk/shrMjIEMhWsbKnFqT"
                target="_blank"
                rel="noopener noreferrer"
              />
            }
          >
            Join Slack
          </Button>
          <Button
            variant="tertiary"
            size="base"
            onClick={() => {
              window.location.assign(routes.shops.default);
            }}
          >
            Go to my dashboard
          </Button>
        </div>
        <div className="w-full max-w-md">
          <BetaIntroVideo />
        </div>
      </motion.div>
    </section>
  );
};

const createStampAudioContext = (): AudioContext | null => {
  const webkitAudioContext: typeof AudioContext | undefined = Reflect.get(
    window,
    "webkitAudioContext",
  );
  const AudioContextConstructor = window.AudioContext ?? webkitAudioContext;
  return AudioContextConstructor ? new AudioContextConstructor() : null;
};

const playStampThud = (audioContext: AudioContext) => {
  const startTime = audioContext.currentTime;
  const compressor = audioContext.createDynamicsCompressor();
  compressor.threshold.value = -18;
  compressor.knee.value = 12;
  compressor.ratio.value = 8;
  compressor.attack.value = 0.002;
  compressor.release.value = 0.16;
  compressor.connect(audioContext.destination);

  const thud = audioContext.createOscillator();
  const thudGain = audioContext.createGain();
  thud.type = "sine";
  thud.frequency.setValueAtTime(125, startTime);
  thud.frequency.exponentialRampToValueAtTime(48, startTime + 0.16);
  thudGain.gain.setValueAtTime(0.55, startTime);
  thudGain.gain.exponentialRampToValueAtTime(0.0001, startTime + 0.18);
  thud.connect(thudGain).connect(compressor);
  thud.start(startTime);
  thud.stop(startTime + 0.18);

  const noiseDuration = 0.13;
  const noiseBuffer = audioContext.createBuffer(
    1,
    Math.floor(audioContext.sampleRate * noiseDuration),
    audioContext.sampleRate,
  );
  const noiseData = noiseBuffer.getChannelData(0);
  for (let frame = 0; frame < noiseData.length; frame += 1) {
    const envelope = 1 - frame / noiseData.length;
    noiseData[frame] = (Math.random() * 2 - 1) * envelope;
  }

  const paperImpact = audioContext.createBufferSource();
  const paperFilter = audioContext.createBiquadFilter();
  const paperGain = audioContext.createGain();
  paperImpact.buffer = noiseBuffer;
  paperFilter.type = "lowpass";
  paperFilter.frequency.value = 1100;
  paperFilter.Q.value = 0.8;
  paperGain.gain.setValueAtTime(0.34, startTime);
  paperGain.gain.exponentialRampToValueAtTime(
    0.0001,
    startTime + noiseDuration,
  );
  paperImpact.connect(paperFilter).connect(paperGain).connect(compressor);
  paperImpact.start(startTime);
};

// NOTE (Yuxin, 2026-07-18): Browsers gate audio behind a user gesture, so `unlock` warms the AudioContext on first interaction and `playStamp` fires once on stamp impact.
const useStampAudio = () => {
  const audioContextRef = React.useRef<AudioContext | null>(null);
  const hasPlayedRef = React.useRef(false);

  const unlock = () => {
    try {
      const audioContext = audioContextRef.current ?? createStampAudioContext();
      if (!audioContext) {
        return;
      }
      audioContextRef.current = audioContext;
      if (audioContext.state === "suspended") {
        void audioContext.resume().catch(() => undefined);
      }
    } catch {
      audioContextRef.current = null;
    }
  };

  const playStamp = () => {
    if (hasPlayedRef.current) {
      return;
    }
    hasPlayedRef.current = true;

    const audioContext = audioContextRef.current;
    if (!audioContext) {
      return;
    }

    const play = () => {
      playStampThud(audioContext);
    };
    if (audioContext.state === "suspended") {
      void audioContext
        .resume()
        .then(play)
        .catch(() => undefined);
      return;
    }
    play();
  };

  return { unlock, playStamp };
};

const AgentBetaWaitlistPage: React.FC = () => {
  const [searchParams] = useSearchParams();
  const profileTitle = searchParams.get("title")?.trim() || undefined;

  return (
    <AgentBetaPageShell>
      <AgentBetaWaitlistForm variant="page" profileTitle={profileTitle} />
    </AgentBetaPageShell>
  );
};

export default AgentBetaWaitlistPage;
